import React from 'react';
import { FiSave, FiTrash2, FiClock, FiAlertTriangle, FiX } from 'react-icons/fi';
import { DraftRecoveryState } from '../../utils/hooks/programs/useDraftRecovery';

interface DraftRecoveryModalProps {
  state: DraftRecoveryState;
  onRecover: () => void;
  onDiscard: () => void;
  onHide: () => void;
  getDraftAge: () => string;
  getDraftType: () => string;
}

/**
 * Modal for draft recovery options
 * Allows users to recover or discard saved draft data
 */
const DraftRecoveryModal: React.FC<DraftRecoveryModalProps> = ({
  state,
  onRecover,
  onDiscard,
  onHide,
  getDraftAge,
  getDraftType
}) => {
  if (!state.showPrompt || !state.draftData) {
    return null;
  }

  const draftType = getDraftType();
  const draftAge = getDraftAge();
  const isErrorRecovery = state.draftData.recoveryTimestamp;
  const hasError = state.draftData.error;

  // Check if draft contains file uploads that will be lost
  const hasFileUploads = state.draftData.data && (
    (state.draftData.data.profilePicture && Array.isArray(state.draftData.data.profilePicture) &&
     state.draftData.data.profilePicture.some((item: any) => item._isFile)) ||
    (state.draftData.data.attachments && Array.isArray(state.draftData.data.attachments) &&
     state.draftData.data.attachments.some((item: any) => item._isFile))
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="draft-recovery-title"
      aria-describedby="draft-recovery-description"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`
                p-2 rounded-lg
                ${isErrorRecovery
                  ? 'bg-orange-100 text-orange-600'
                  : hasError
                    ? 'bg-red-100 text-red-600'
                    : 'bg-blue-100 text-blue-600'
                }
              `}>
                {isErrorRecovery ? (
                  <FiAlertTriangle className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <FiSave className="h-5 w-5" aria-hidden="true" />
                )}
              </div>
              <div>
                <h3 id="draft-recovery-title" className="text-lg font-semibold text-gray-900">
                  {isErrorRecovery ? 'Recover Your Work' : 'Draft Found'}
                </h3>
                <p className="text-sm text-gray-600">
                 Looks like you were working on this earlier.
                </p>
              </div>
            </div>
            <button
              onClick={onHide}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close draft recovery dialog"
              type="button"
            >
              <FiX className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div id="draft-recovery-description" className="mb-6">
            {isErrorRecovery ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <p className="text-orange-800 text-sm">
                  We detected that the editor encountered an error previously.
                  Your work has been preserved and can be recovered.
                </p>
                {state.draftData.errorId && (
                  <p className="text-orange-600 text-xs mt-2">
                    Error ID: {state.draftData.errorId}
                  </p>
                )}
              </div>
            ) : hasError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 text-sm">
                  This draft was created when a save operation failed:
                </p>
                <p className="text-red-600 text-xs mt-1">
                  {state.draftData.error}
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                 We saved your progress automatically.<br></br>
                 Do you want to continue where you left off?
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiClock className="h-4 w-4" />
              <span>Last saved: {new Date(state.draftData.timestamp).toLocaleString()}</span>
            </div>

            {/* File upload warning */}
            {hasFileUploads && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">File uploads detected</p>
                    <p className="text-xs mt-1">
                      Logo images and attachments cannot be restored from drafts.
                      You'll need to re-upload these files after recovery.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3" role="group" aria-label="Draft recovery actions">
            <button
              onClick={onRecover}
              disabled={state.isRecovering}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              aria-label={state.isRecovering ? 'Recovering draft data...' : 'Recover draft and restore form data'}
              type="button"
            >
              <FiSave className="h-4 w-4" aria-hidden="true" />
              {state.isRecovering ? 'Recovering...' : 'Recover Draft'}
            </button>

            <button
              onClick={onDiscard}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              aria-label="Discard draft and remove saved data"
              type="button"
            >
              <FiTrash2 className="h-4 w-4" aria-hidden="true" />
              Discard
            </button>
          </div>

          {/* Additional info */}
          {/* <p className="text-xs text-gray-500 mt-4 text-center">
            You can always access draft recovery from the editor menu
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default DraftRecoveryModal;
